import styles from "./WhackGame.module.less";
import DownTime from "../components/DownTime";
import NoticeText from "../components/NoticeText";
import DSBridge from "dsbridge";
import { useState, useEffect, useRef } from "react";
import { getGameAudio, getMaterialsByGame } from "@/api/mine";
import { useSearchParams } from "react-router-dom";
import { Toast } from "react-vant";
import { showDeviceModal } from "views/mine/device-manage/components/device-modal";
import { 
  tSetTimeout, 
  tClearTimeout, 
  tAddGlobalListeners, 
  tRemoveGlobalListeners 
} from "../components/TimestampTimer";

const WhackGame = () => {
  const [searchParams] = useSearchParams();
  const [isVisible, setIsVisible] = useState(true);
  const [activeMoles, setActiveMoles] = useState({});
  const [correctCount, setCorrectCount] = useState(0); // 使用state记录正确点击次数
  const [correntItem, setCorrentItem] = useState({});
  const [isStart, setIsStart] = useState(false);

  const explanationRef = useRef(null);
  const downTimeRef = useRef(null);
  const gameWordsRef = useRef([]);
  const moleTimersRef = useRef([]);
  const currentMoleIndexRef = useRef(0);
  const lastHoleRef = useRef(null); // 记录上一个洞的位置
  const hasInteractionRef = useRef(false); // 记录是否有点击操作

  const handleClose = () => {
    showDeviceModal({
      message:
        "确定退出课堂练习？中途退出无法获得积分哦~退出后将继续学习课程。",
      onConfirm: () => {
        DSBridge.call("stopSound", "bgm_sound");
        DSBridge.call("exitWebView");
        tRemoveGlobalListeners(); // 确保退出时清理监听器
      },
      isShowCancel: true,
      isShowConfirm: true,
      cancelText: "取消",
      confirmText: "退出",
    });
  };

  const handleGetAudio = async (text) => {
    try {
      const res = await getGameAudio({
        character_id: searchParams.get("character_id"),
        token: searchParams.get("token"),
        text: text,
      });
      if (res.code === 200 && res.data?.url) {
        return res.data.url;
      }
      throw new Error("Failed to get audio URL");
    } catch (error) {
      throw error;
    }
  };

  // 提取播放音频和退出的公共逻辑
  const playAudioAndExit = async (explanationText, score = 0) => {
    try {
      const audioUrl = await handleGetAudio(explanationText);
      DSBridge.call(
        "playOnlineSound",
        `${process.env.REACT_APP_API_CDN}/${audioUrl}`,
        () => {
          if (score > 0) {
            DSBridge.call("addScore", score.toString());
          }
          DSBridge.call("exitWebView");
        }
      );
    } catch {
      if (score > 0) {
        DSBridge.call("addScore", score.toString());
      }
      DSBridge.call("exitWebView");
    }
  };

  // 打乱数组的函数 (Fisher-Yates洗牌算法)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // 初始化单词数组
  const initializeWords = (words) => {
    const duplicatedWords = [];

    // 每个单词拷贝4份，总共20个单词
    words.forEach((word) => {
      for (let i = 0; i < 4; i++) {
        duplicatedWords.push({
          ...word,
          id: `${word.text}_${i}`, // 添加唯一标识
        });
      }
    });

    // 打乱顺序
    const shuffledWords = shuffleArray(duplicatedWords);

    console.log("初始化单词:", shuffledWords);
    return shuffledWords;
  };

  // 计算地鼠出现间隔
  const calculateMoleInterval = (duration, totalMoles = 20) => {
    // 最后一个地鼠需要2秒动画时间，所以在duration-2秒时出现
    // 间隔 = (duration - 2) / (totalMoles - 1)
    return (duration - 2) / (totalMoles - 1);
  };

  // 获取可用的洞（排除已有地鼠的洞和上一个洞）
  const getAvailableHole = () => {
    const totalHoles = [1, 2, 3, 4, 5];
    const occupiedHoles = Object.values(activeMoles).map(
      (mole) => mole.holeNumber
    );

    // 排除已占用的洞和上一个洞
    let availableHoles = totalHoles.filter(
      (hole) => !occupiedHoles.includes(hole) && hole !== lastHoleRef.current
    );

    // 如果没有可用洞（排除上一个洞后），则只排除已占用的洞
    if (availableHoles.length === 0) {
      availableHoles = totalHoles.filter(
        (hole) => !occupiedHoles.includes(hole)
      );
      console.warn("排除上一个洞后无可用洞，只排除已占用洞");
    }

    // 如果所有洞都被占用，返回随机洞（这种情况理论上不应该发生）
    if (availableHoles.length === 0) {
      console.warn("所有洞都被占用，随机选择一个洞");
      return totalHoles[Math.floor(Math.random() * totalHoles.length)];
    }

    // 从可用洞中随机选择
    const selectedHole =
      availableHoles[Math.floor(Math.random() * availableHoles.length)];

    // 更新上一个洞的记录
    lastHoleRef.current = selectedHole;

    return selectedHole;
  };

  // 处理地鼠点击事件
  const handleMoleClick = (moleKey) => {
    console.log("点击地鼠:", moleKey);

    // 记录有操作
    hasInteractionRef.current = true;

    setActiveMoles((prev) => {
      const currentMole = prev[moleKey];

      // 如果地鼠已被点击，不再处理
      if (!currentMole || currentMole.clicked) {
        return prev;
      }

      // 更新地鼠状态
      const updatedMole = {
        ...currentMole,
        clicked: true,
        clickResult: currentMole.isCorrect ? "correct" : "incorrect",
      };

      if (currentMole.isCorrect) {
        DSBridge.call("playSound", "correct_sound");
      } else {
        DSBridge.call("playSound", "aou_sound");
      }
      console.log(
        `地鼠被点击: ${currentMole.text}, 结果: ${updatedMole.clickResult}`
      );

      // 如果点击正确，增加正确计数
      if (currentMole.isCorrect) {
        setCorrectCount((prevCount) => {
          const newCount = prevCount + 1;
          console.log(`正确点击次数: ${newCount}`);
          return newCount;
        });
      }

      return {
        ...prev,
        [moleKey]: updatedMole,
      };
    });
  };

  // 显示地鼠
  const showMole = (moleData) => {
    // 使用可用洞而不是完全随机
    const availableHole = getAvailableHole();
    const moleKey = `${availableHole}_${Date.now()}`;

    console.log(`地鼠出现在${availableHole}号洞: ${moleData.text}`);

    setActiveMoles((prev) => ({
      ...prev,
      [moleKey]: {
        ...moleData,
        holeNumber: availableHole,
        showTime: Date.now(),
        clicked: false,
        clickResult: null,
      },
    }));

    // 2秒后隐藏地鼠
    tSetTimeout(() => {
      setActiveMoles((prev) => {
        const newMoles = { ...prev };
        delete newMoles[moleKey];
        return newMoles;
      });
    }, 2000);
  };

  // 开始地鼠游戏
  const startMoleGame = (duration, words) => {
    const totalMoles = 20;
    const interval = calculateMoleInterval(duration, totalMoles);

    console.log(`游戏时长: ${duration}秒, 地鼠间隔: ${interval.toFixed(2)}秒`);

    // 清除之前的定时器和状态
    moleTimersRef.current.forEach((timer) => tClearTimeout(timer));
    moleTimersRef.current = [];
    currentMoleIndexRef.current = 0;
    lastHoleRef.current = null; // 重置上一个洞的记录
    setCorrectCount(0); // 重置正确计数
    hasInteractionRef.current = false; // 重置操作记录

    // 创建地鼠出现的定时器
    for (let i = 0; i < totalMoles; i++) {
      const timer = tSetTimeout(
        () => {
          if (currentMoleIndexRef.current < words.length) {
            const moleData = words[currentMoleIndexRef.current];

            console.log(`第${i + 1}个地鼠准备出现: ${moleData.text}`);
            showMole(moleData);

            currentMoleIndexRef.current++;
          }
        },
        i * interval * 1000
      );

      moleTimersRef.current.push(timer);
    }
  };

  const handleTimeEnd = () => {
    // 清除所有定时器
    moleTimersRef.current.forEach((timer) => tClearTimeout(timer));
    moleTimersRef.current = [];
    lastHoleRef.current = null; // 重置上一个洞的记录

    setIsStart(false);
    DSBridge.call("stopSound", "bgm_sound");
    // 游戏结束时输出最终成绩和操作状态
    console.log(`游戏结束！`);
    console.log(`最终正确点击次数: ${correctCount}`);
    console.log(`是否有操作: ${hasInteractionRef.current}`);

    if (hasInteractionRef.current) {
      const score = correctCount * 2;
      const svgaName = score === 8 ? "outstanding" : "nice_try";
      const explanationText =
        score === 8
          ? explanationRef.current?.correct
          : explanationRef.current?.incorrect;
      DSBridge.call("playSVGA", svgaName, () => {
        playAudioAndExit(explanationText, score);
      });
    } else {
      DSBridge.call("playSVGA", "missed", () => {
        playAudioAndExit(explanationRef.current?.noAnswer);
      });
    }
  };
  // 渲染地鼠的函数
  const renderMoleInHole = (holeNumber) => {
    const moleEntry = Object.entries(activeMoles).find(
      ([key, mole]) => mole.holeNumber === holeNumber
    );

    if (!moleEntry) return null;
    const [moleKey, activeMole] = moleEntry;

    // 处理触摸事件
    const handleTouchStart = (e) => {
      // 如果已经被点击过，不再处理
      if (activeMole.clicked) return;

      if (!isStart) return;

      handleMoleClick(moleKey);
    };
    return (
      <div className={styles["mole-box"]}>
        <div
          className={`${styles["mole-box-item"]} ${styles["mole-box-item--move"]}`}
          onTouchStart={handleTouchStart}
          style={{
            userSelect: "none",
            touchAction: "manipulation", // 添加这个CSS属性
          }}
        >
          <div className={styles["item-text"]}>{activeMole.text}</div>

          {/* 根据点击结果显示不同的元素 */}
          {activeMole.clicked && activeMole.clickResult === "correct" && (
            <div className={styles["item-hammer"]}></div>
          )}

          {activeMole.clicked && activeMole.clickResult === "incorrect" && (
            <div className={styles["item-wrong"]}></div>
          )}
        </div>
      </div>
    );
  };
  const renderNoStart = () => {
    return (
      <>
        <div className={styles["hole-1"]}>
          <div className={styles["mole-box"]}>
            <div
              className={`${styles["mole-box-item"]} ${styles["mole-box-item--no-move"]}`}
            >
              <div className={styles["item-text"]}>{correntItem.text}</div>
            </div>
          </div>
        </div>
        <div className={styles["hole-2"]}>
          <div className={styles["mole-box"]}>
            <div
              className={`${styles["mole-box-item"]} ${styles["mole-box-item--no-move"]}`}
            >
              <div className={styles["item-text"]}>{correntItem.text}</div>
            </div>
          </div>
        </div>
        <div className={styles["hole-3"]}>
          <div className={styles["mole-box"]}>
            <div
              className={`${styles["mole-box-item"]} ${styles["mole-box-item--no-move"]}`}
            >
              <div className={styles["item-text"]}>{correntItem.text}</div>
            </div>
          </div>
        </div>
        <div className={styles["hole-4"]}>
          <div className={styles["mole-box"]}>
            <div
              className={`${styles["mole-box-item"]} ${styles["mole-box-item--no-move"]}`}
            >
              <div className={styles["item-text"]}>{correntItem.text}</div>
            </div>
          </div>
        </div>
        <div className={styles["hole-5"]}></div>
      </>
    );
  };
  const initGame = async () => {
    // const res = {
    //   code: 200,
    //   status: true,
    //   data: {
    //     type: 14,
    //     question: "Time to practice!",
    //     question_zh: "",
    //     answer: "",
    //     image:
    //       "https://cdn03.myaitalk.vip/utalkrc/materials/slides/test_test/test_test-unit_1_tts_test_111301/edit_image_4ad46057-2511-4e1d-8dc6-06a575c12046.png",
    //     practice_config: {
    //       type: "mole",
    //       guide: "Whack the correct word! Which one means the time after lunch",
    //       duration: 30,
    //       explanation: {
    //         correct:
    //           "Boom! You got afternoon! That's the time after lunch — nice hit!",
    //         incorrect:
    //           "Bonk! That's not afternoon! Try again — afternoon comes after 12!",
    //         noAnswer:
    //           "The mole got away! The word was afternoon, the time after lunch.",
    //       },
    //       config: {
    //         words: [
    //           {
    //             text: "morning",
    //             isCorrect: false,
    //           },
    //           {
    //             text: "afternoon",
    //             isCorrect: true,
    //           },
    //           {
    //             text: "evening",
    //             isCorrect: false,
    //           },
    //           {
    //             text: "lunchtime",
    //             isCorrect: false,
    //           },
    //           {
    //             text: "midnight",
    //             isCorrect: false,
    //           },
    //         ],
    //       },
    //     },
    //   },
    //   message: "success",
    // };
    try {
      const res = await getMaterialsByGame({
        material_id: searchParams.get("material_id"),
        token: searchParams.get("token"),
        unit_id: searchParams.get("unit_id"),
      });
      if (res.code === 200) {
        const config = res.data?.practice_config;
        if (config?.type !== "mole") {
          Toast.info("游戏类型错误，请点击左上角退出");
          return;
        }
        explanationRef.current = config?.explanation;
        setCorrentItem(config.config.words.find((item) => item.isCorrect));
        // 初始化单词数组
        const initializedWords = initializeWords(config.config.words);
        gameWordsRef.current = initializedWords;
        try {
          const audioUrl = await handleGetAudio(config?.guide);
          DSBridge.call(
            "playOnlineSound",
            `${process.env.REACT_APP_API_CDN}/${audioUrl}`,
            () => {
              downTimeRef.current?.startCountdown(config?.duration);
              setIsStart(true);
              // 开始地鼠游戏
              startMoleGame(config.duration, initializedWords);
            }
          );
        } catch {
          downTimeRef.current?.startCountdown(config?.duration);
          setIsStart(true);
          // 开始地鼠游戏
          startMoleGame(config.duration, initializedWords);
        }
      } else {
        Toast.info("请求游戏数据失败，请点击左上角退出");
      }
    } catch {
      Toast.info("验证游戏数据失败，请点击左上角退出");
    }
  };

  // 清理函数
  useEffect(() => {
    DSBridge.call("playSound", "bgm_sound");
    DSBridge.call("playSound", "start_sound");
    tSetTimeout(() => {
      setIsVisible(false);
    }, 1000);
    initGame();
    return () => {
      DSBridge.call("stopSound", "bgm_sound");
      // 清除所有定时器
      moleTimersRef.current.forEach((timer) => tClearTimeout(timer));
      lastHoleRef.current = null;
    };
  }, []);

  // 添加 useEffect 来管理全局监听器
  useEffect(() => {
    console.log("🔧 添加TimestampTimer全局监听器");
    
    // 添加全局监听器
    tAddGlobalListeners();

    return () => {
      // 移除全局监听器
      tRemoveGlobalListeners();
    };
  }, []);

  return (
    <div className={styles["whack-game-container"]}>
      {isVisible && (
        <div className={styles["start-box"]}>
          <img
            src={require("@/images/new-game/common/start.png")}
            className={styles["start-cover"]}
          />
        </div>
      )}
      <div className={`${isVisible ? styles["visibility-box"] : ""}`}>
        <div className={styles["back-bottom"]}></div>
        <div className={styles["back-top"]}>
          <div className={styles["top-light"]}>
            <div className={styles["top-light-content"]}>
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  className={`${styles["item-light"]} ${
                    correctCount > index
                      ? styles["item-light--open"]
                      : styles["item-light--close"]
                  }`}
                  key={index}
                ></div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles["hole-box"]}>
          {isStart ? (
            <>
              <div className={styles["hole-1"]}>{renderMoleInHole(1)}</div>
              <div className={styles["hole-2"]}>{renderMoleInHole(2)}</div>
              <div className={styles["hole-3"]}>{renderMoleInHole(3)}</div>
              <div className={styles["hole-4"]}>{renderMoleInHole(4)}</div>
              <div className={styles["hole-5"]}>{renderMoleInHole(5)}</div>
            </>
          ) : (
            renderNoStart()
          )}
        </div>
        <DownTime ref={downTimeRef} onTimeEnd={handleTimeEnd} />
        <NoticeText text="Click 听一听，点击正确的地鼠吧！" />
      </div>
      <div className={styles["close-icon"]} onClick={handleClose}>
        <img
          className={styles["close-icon-img"]}
          src={require("@/images/new-game/common/close.png")}
        />
      </div>
    </div>
  );
};

export default WhackGame;
